import { useEffect, useRef, useState } from "react";
import { BrowserQRCodeReader } from "@zxing/browser";
import type { IScannerControls } from "@zxing/browser";

type QrScannerProps = {
  onDetected: (text: string) => void;
  onClose: () => void;
};

// The per-frame decode callback fires on every frame, successful or not.
// A failed frame throws one of these three -- all expected/routine while
// the camera is still hunting for a fully-in-view, well-focused code, not
// an actual problem. Only NotFoundException was filtered before, so a
// ChecksumException/FormatException on a blurry or partial frame -- normal
// during live scanning -- surfaced as a persistent "camera error" even
// though the camera and scanning were both working fine.
const EXPECTED_PER_FRAME_ERRORS = new Set([
  "NotFoundException",
  "ChecksumException",
  "FormatException",
]);

export function QrScanner({ onDetected, onClose }: QrScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const reader = new BrowserQRCodeReader();
    let cancelled = false;

    reader
      .decodeFromVideoDevice(undefined, videoRef.current ?? undefined, (result, err, controls) => {
        controlsRef.current = controls;
        if (cancelled) return;
        if (result) {
          controls.stop();
          onDetected(result.getText());
        }
        if (err && !EXPECTED_PER_FRAME_ERRORS.has(err.name)) {
          setError(err.message || "Camera error");
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Could not access camera");
        }
      });

    return () => {
      cancelled = true;
      controlsRef.current?.stop();
    };
  }, [onDetected]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div className="qr-scanner-overlay" role="dialog" aria-modal="true" aria-label="Scan visitor badge">
      <div className="qr-scanner-video">
        <video ref={videoRef} style={{ width: "100%", display: "block" }} muted playsInline />
      </div>
      {error ? <p className="error-banner">{error}</p> : <p style={{ color: "#f9fbfc" }}>Point the camera at a badge QR code</p>}
      <button type="button" className="secondary-btn" onClick={onClose}>
        Cancel <span className="key-hint">Esc</span>
      </button>
    </div>
  );
}
